import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import {
  createReportFolder,
  readReportFolderInformation,
  readReportFolders,
  updateReportFolder,
} from '@api/reports';
import {
  Announcement,
  Button,
  Input,
  isMaxNameLength,
  ModalV2 as Modal,
  Radio,
  Select,
  truncateTextWithEllipsis,
} from '@utilities';

import styles from '../../../../../../../../pages/Workspace/components/ListPage/components/ActionsToolbar/components/AddToFolder/_index.module.scss';

const ModalAddToFolder = ({ onClose, reportId }) => {
  const OPTIONS = {
    EXISTING: 'EXISTING',
    NEW: 'NEW',
  };

  const [error, setError] = useState();
  const [folderInformation, setFolderInformation] = useState(null);
  const [folders, setFolders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedOption, setSelectedOption] = useState(OPTIONS.EXISTING);

  const isNewFolderSelected = selectedOption === OPTIONS.NEW;
  const onLoadSignal = useRef(new AbortController());

  const handleSubmit = async () => {
    setError(null);
    try {
      setIsSubmitting(true);

      let response;
      if (isNewFolderSelected) {
        response = await createReportFolder({
          name: newFolderName,
        });
      }

      await updateReportFolder({
        folderId: isNewFolderSelected ? response?.data?.id : selectedFolder?.value,
        ids: [folderInformation?.folderEntryId],
      });

      onClose();
    } catch (error) {
      setError(error?.response?.data);
      console.error(error);
      setIsSubmitting(false);
    }
  };

  const getFolders = async () => {
    try {
      const [folderInformationResponse, foldersResponse] = await Promise.all([
        readReportFolderInformation({ reportId, signal: onLoadSignal.current.signal }),
        readReportFolders({ signal: onLoadSignal.current.signal }),
      ]);

      setFolderInformation(folderInformationResponse?.data);
      const data = foldersResponse?.data?.folders?.map((folder) => {
        return { label: truncateTextWithEllipsis({ text: folder?.name }), value: folder?.id };
      });
      setFolders(
        !folderInformationResponse?.data?.isAtRoot
          ? [{ label: 'All Reports', value: 'root' }, ...data]
          : data
      );
      setSelectedFolder(data[0]);
    } catch (error) {
      setError(error?.response?.data);
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getFolders();

    return () => onLoadSignal?.current.abort();
  }, []);

  return (
    <Modal
      buttons={[
        <Button key="cancel-btn" onClick={onClose} text="Cancel" variant="secondary" />,
        <Button
          isDisabled={
            isNewFolderSelected
              ? isMaxNameLength(newFolderName) || newFolderName.trim().length === 0
              : !selectedFolder
          }
          isLoading={isLoading || isSubmitting}
          key="submit-btn"
          onClick={handleSubmit}
          text="Save"
        />,
      ]}
      className={styles['add-to-folder']}
      isActive
      onClose={onClose}
      title="Add to Folder"
    >
      {error && <Announcement text={error} variant="error" />}
      <p>Create a new folder or move to an existing one.</p>
      <ul>
        <li>
          <Radio
            isChecked={selectedOption === OPTIONS.EXISTING}
            label="Existing Folder"
            onChange={setSelectedOption}
            value={OPTIONS.EXISTING}
          />
          {selectedOption === OPTIONS.EXISTING && (
            <Select
              isLoading={isLoading}
              onChange={setSelectedFolder}
              options={folders}
              value={selectedFolder}
            />
          )}
        </li>
        <li>
          <Radio
            isChecked={isNewFolderSelected}
            label="New Folder"
            onChange={setSelectedOption}
            value={OPTIONS.NEW}
          />
          {isNewFolderSelected && (
            <Input
              error={
                isMaxNameLength(newFolderName)
                  ? 'Maximum number of characters reached. Try something shorter.'
                  : ''
              }
              onChange={setNewFolderName}
              placeholder="Enter name"
              value={newFolderName}
            />
          )}
        </li>
      </ul>
    </Modal>
  );
};

ModalAddToFolder.propTypes = {
  onClose: PropTypes.func.isRequired,
  reportId: PropTypes.number.isRequired,
};

export default ModalAddToFolder;
